import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useSession } from '@/hooks/useSession';
import { useT } from '@/i18n/context';

/**
 * Gate for the screens that need a session.
 *
 * This is convenience, not security — every protected route is enforced again
 * by `auth:sanctum` on the API. What it buys is not opening the camera or
 * firing requests that can only come back 401.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const t = useT();
  const { data: user, isPending, error } = useSession();

  if (isPending) {
    return (
      <main className="grid min-h-dvh place-items-center p-6">
        <p className="text-sm opacity-60">{t('common.loading')}</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="grid min-h-dvh place-items-center p-6">
        <p className="max-w-sm rounded-lg bg-red-500/10 p-4 text-center text-sm text-red-500">
          {t('common.api_unreachable', { message: error.message })}
        </p>
      </main>
    );
  }

  if (!user) {
    // `state` so signing in can return the user to where they were headed, and
    // `replace` so Back does not bounce them through the guard again.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
}
